import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { classes, st, vars } from './WowImage.st.css';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { Image as WixImage, initCustomElement } from '@wix/image';
import { calculateDimensions, resolveFocalPointCoordinates, } from '../Image/ImageUtils';
import { Text } from '../Text';
import { Spinner, SpinnerTypes } from '../Spinner';
import { ImageResizeOptions, ImageAspectRatioPresets, ImageLoadingBehaviorOptions, ImageHoverEffectOptions, } from '../Image/types';
import { extractWixMediaId } from './utils';
const isTestEnv = process.env.NODE_ENV === 'test';
if (isTestEnv && typeof window !== 'undefined' && !window.ResizeObserver) {
    window.ResizeObserver = class Observer {
        /* eslint-disable-next-line */
        constructor() { }
        disconnect() { }
        observe() { }
        unobserve() { }
    };
}
/** Image is a component to literally display an image - whether an absolute with full URL or a media platform item with relative URI */
export const WowImage = ({ src, alt, fluid, width, height, resize = ImageResizeOptions.contain, onLoad, onError, className, focalPoint, aspectRatio, hoverEffect, sourceWidth, sourceHeight, errorMessage = 'Image not found', loadingBehavior, stretchImage = true, shouldUseLQIP, isInFirstFold, isSEOBot, forceImageContain = false, containerAspectRatio, ...imageProps }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    useEffect(() => {
        initCustomElement();
    }, []);
    useEffect(() => {
        const { current: imageElement } = imageRef;
        // Updating the state in case it's rendered with SSR and the image is loaded on the client even before registering the `onLoad` event
        if (!isLoaded && imageElement?.complete) {
            setIsLoaded(true);
        }
    }, [isLoaded]);
    const handleLoad = (event) => {
        setIsLoaded(true);
        setIsError(false);
        onLoad?.(event);
    };
    const handleError = (event) => {
        if (!isError) {
            setIsError(true);
        }
        onError?.(event);
    };
    const imageSrc = extractWixMediaId(src) || src;
    const isAbsoluteUrl = imageSrc && imageSrc.match('^(data:image|(blob:)?https?://)');
    const isBlurLoading = loadingBehavior === ImageLoadingBehaviorOptions.blur;
    const hasLoadingEffect = loadingBehavior && loadingBehavior !== ImageLoadingBehaviorOptions.none;
    const zoomOnHover = hoverEffect === ImageHoverEffectOptions.zoom;
    const aspectRatioAsNumber = ImageAspectRatioPresets[aspectRatio] || aspectRatio;
    const focalPointCoordinates = focalPoint && resolveFocalPointCoordinates(focalPoint);
    // Calculating the dimensions considering the given values and aspect ratio
    const calculatedDimensions = calculateDimensions({
        width,
        height,
        aspectRatio: aspectRatioAsNumber,
    });
    const calculatedDimensionsWithHover = calculatedDimensions
        ? {
            width: calculatedDimensions?.width * (zoomOnHover ? 1.07 : 1),
            height: calculatedDimensions?.height * (zoomOnHover ? 1.07 : 1),
        }
        : null;
    const shouldUseOldImageDims = resize === ImageResizeOptions.contain && fluid;
    const calculatedContainerAspectRatio = calculatedDimensions?.width / calculatedDimensions?.height;
    return (React.createElement("div", { ref: containerRef, "data-aspect-ratio": aspectRatio, "data-source-width": sourceWidth, "data-source-height": sourceHeight, "data-resize": resize, "data-use-lqip": shouldUseLQIP, "data-first-fold": isInFirstFold, "data-is-seo-bot": isSEOBot, className: st(classes.root, {
            resize,
            focalPoint: isAbsoluteUrl && Boolean(focalPointCoordinates),
            fluid,
            hoverEffect,
            loadSpinner: loadingBehavior === ImageLoadingBehaviorOptions.spinner,
            loaded: hasLoadingEffect && isLoaded,
            isError,
            noImage: !imageSrc,
            stretchImage,
            forceImageContain,
            horizontalContainer: (containerAspectRatio ?? calculatedContainerAspectRatio) > 1,
            verticalContainer: (containerAspectRatio ?? calculatedContainerAspectRatio) <= 1,
        }, className), style: {
            ...(!fluid && !forceImageContain && calculatedDimensions),
            ...(isAbsoluteUrl &&
                focalPointCoordinates && {
                [vars.focalPointX]: `${focalPointCoordinates.x}%`,
                [vars.focalPointY]: `${focalPointCoordinates.y}%`,
            }),
            ...(!stretchImage &&
                Boolean(sourceWidth) &&
                Boolean(sourceHeight) && {
                '--wut-source-width': `${sourceWidth}px`,
                '--wut-source-height': `${sourceHeight}px`,
            }),
        }, "data-hook": imageProps['data-hook'] },
        src ? (React.createElement(WixImage, { ...imageProps, uri: imageSrc, name: imageSrc, width: sourceWidth || (shouldUseOldImageDims && width) || undefined, height: sourceHeight || (shouldUseOldImageDims && height) || undefined, displayMode: resize === 'cover' ? 'fill' : 'fit', alt: alt, focalPoint: focalPointCoordinates, containerWidth: calculatedDimensionsWithHover?.width, containerHeight: calculatedDimensionsWithHover?.height, ...(zoomOnHover && {
                targetWidth: calculatedDimensionsWithHover?.width,
                targetHeight: calculatedDimensionsWithHover?.height,
                skipMeasure: true,
            }), className: classes.image, onError: handleError, onLoad: handleLoad, placeholderTransition: isBlurLoading ? 'blur' : null, shouldUseLQIP: shouldUseLQIP ? true : isBlurLoading, isInFirstFold: isInFirstFold, isSEOBot: isSEOBot })) : null,
        isError && (React.createElement("div", { className: classes.errorWrapper },
            React.createElement(Error, { className: classes.errorIcon }),
            React.createElement(Text, { className: classes.errorMessage },
                React.createElement("span", { className: classes.srError }, alt),
                errorMessage))),
        loadingBehavior === ImageLoadingBehaviorOptions.spinner ? (React.createElement("div", { className: classes.spinnerOverlay, "aria-hidden": "true" },
            React.createElement(Spinner, { isCentered: true, diameter: 24, type: SpinnerTypes.regular, className: classes.spinner }))) : null));
};
//# sourceMappingURL=WowImage.js.map